//dependencies
import React, { useState } from "react";
import { useParams } from "react-router-dom";

// components
import Button from "../../../../../components/Button";
import Pagination from "../../../../../components/Pagination";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import Spacer from "../../../../../components/Spacer";
import ScrollableTable from "../../../../../components/TableLite/TableLiteScrollable";
import getReviewCycleTableCols from "./components/ReviewCycleTableCols";
import ReviewCyclesFilters from "./components/ReviewCyclesFilters";
import ReviewCycleReadableFiltersList from "./components/ReviewCyclesReadableFilters";

// hooks
import { useSearchService, useSearchServicePrefetch } from "../../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

// types
import { SERVICE_REVIEW_CYCLE } from "../../../../../api/services/defs/search-service";
import { ReviewCycle } from "../../../../../api/services/searchServiceResultTypes/reviewCycles";

// utils
import { getFiltersForSearchService } from "./utils";

//template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
// utils
import { CompanyReviewsNavigationLinks, REVIEWS_TABLE_LIMIT } from "../../constants";

type ReviewCyclesProps = {
	navLinks: SideNavigationLink[];
};

const ReviewCycles = ({ navLinks }: ReviewCyclesProps) => {
	const { page } = useParams<{ page: string }>();

	const {
		pageNumber,
		sort,
		filters,
		setPageNumber,
		setSort,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState({
		defaultSortName: "start_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
	});
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const { data, isLoading } = useSearchService<ReviewCycle>({
		service: SERVICE_REVIEW_CYCLE,
		filters: getFiltersForSearchService(filters, debouncedSearchValue),
		skip: (pageNumber - 1) * REVIEWS_TABLE_LIMIT,
		limit: REVIEWS_TABLE_LIMIT,
		sort,
		defaultErrorMessage: "There was an error getting Review Cycles",
	});

	//prefetch page before and 3 after
	useSearchServicePrefetch<ReviewCycle>(
		{
			service: SERVICE_REVIEW_CYCLE,
			filters: getFiltersForSearchService(filters, debouncedSearchValue),
			skip: (pageNumber - 2) * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
		},
		pageNumber <= 1 ? 0 : 1
	);

	useSearchServicePrefetch<ReviewCycle>(
		{
			service: SERVICE_REVIEW_CYCLE,
			filters: getFiltersForSearchService(filters, debouncedSearchValue),
			skip: pageNumber * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
		},
		3
	);

	const { value: featureFlag } = useFeatureFlagQuery("autoflow", false);

	const reviewCycles = data?.results || [];
	const totalCycles = data?.total || 0;

	const additionalFiltersCount = Object.keys(filters).length;
	const emptyTableState = <EmptySearchTableMessage mainText="No Review Cycles Found" />;

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			activeFiltersCount={additionalFiltersCount}
			searchPlaceholder="Search reviews cycles..."
		/>
	);

	const toolbarInformationRow = (
		<ReviewCycleReadableFiltersList filters={filters} setFilters={(filters) => setFilters({ ...filters })} />
	);

	const drawer = (
		<ReviewCyclesFilters
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => {
				setFilters({ ...newFilters });
			}}
			automationEnabled={featureFlag}
		/>
	);

	const actionButtons = [
		<Button color="green" href="/review-cycle/create/" key="create-review-cycle-button">
			New Review Cycle
		</Button>,
	];

	const mainContent = (
		<div>
			<ScrollableTable
				columns={getReviewCycleTableCols()}
				rows={reviewCycles}
				isLoading={isLoading}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				onColumnClick={(columnID, nextDirection) =>
					setSort({ name: columnID, direction: nextDirection as "asc" | "desc" })
				}
				emptyChildren={emptyTableState}
			/>
			<Spacer height={2} />
			<Pagination
				pageCount={Math.ceil(totalCycles / REVIEWS_TABLE_LIMIT)}
				currentPage={pageNumber}
				onClick={(pageNumber: number) => setPageNumber(pageNumber)}
			/>
		</div>
	);

	return (
		<InfoPageTemplate
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			actionButtons={actionButtons}
			mainContent={mainContent}
			drawer={drawer}
			navigationLinks={navLinks || CompanyReviewsNavigationLinks}
			activeNavigationLinkKey={page}
		/>
	);
};

export default ReviewCycles;
